/**
 * RecipeSourceEditor — full-editor UI for a recipe_source before it
 * becomes a live FoodPulse recipe. Handles Title, Description,
 * Ingredients, Steps, Timings, Cuisine, Difficulty, Diet, Allergens,
 * Tags, Cover Photo, Creator, Publish Date + optional AI-Assist.
 *
 * Two modes:
 *   • inline   → renders inside a card (used from single-URL auto-open)
 *   • modal    → renders as a full-screen dialog (used from bulk queue rows)
 *
 * Save-Draft persists to `recipe_sources.recipe_draft`.
 * Publish converts the source → published `recipes` doc.
 */
import { useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import {
  X, Save, Rocket, Sparkles, Plus, Trash2, GripVertical, ExternalLink,
  AlertTriangle, ChefHat, Loader2, ImageIcon,
} from "lucide-react";

const DIFFICULTIES = ["Easy", "Moderate", "Advanced"];
const DIET_FLAGS   = ["Vegan","Vegetarian","Pescatarian","Gluten-Free","Dairy-Free",
                      "Nut-Free","Keto","Paleo","Low-Carb","Halal","Kosher"];
const ALLERGENS    = ["Milk","Eggs","Peanuts","Tree nuts","Wheat","Gluten","Soy",
                      "Fish","Shellfish","Sesame","Mustard","Celery","Sulphites"];

function slugify(s) {
  return String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 80);
}

export default function RecipeSourceEditor({
  source,          // the recipe_sources doc
  variant = "inline",  // "inline" | "modal"
  onClose,
  onSaved,         // (source) => void — after Save Draft
  onPublished,    // ({ recipe_id, slug }) => void — after Publish
}) {
  const initial = useMemo(() => {
    const draft = source?.recipe_draft || {};
    return {
      title:             draft.title            ?? source?.title ?? "",
      slug:              draft.slug             ?? "",
      short_description: draft.short_description?? source?.description ?? "",
      story:             draft.story            ?? "",
      cover_photo:       draft.cover_photo      ?? source?.thumbnail_url ?? "",
      cuisine:           draft.cuisine          ?? source?.hint_cuisine ?? "",
      country:           draft.country          ?? "",
      category:          draft.category         ?? "",
      difficulty:        draft.difficulty       ?? "Moderate",
      prep_time_min:     draft.prep_time_min    ?? "",
      cook_time_min:     draft.cook_time_min    ?? "",
      total_time_min:    draft.total_time_min   ?? "",
      servings:          draft.servings         ?? "",
      ingredients:       (draft.ingredients?.length ? draft.ingredients : null)
                         || (source?.schema_recipe?.recipeIngredient || []).map((n) => ({ name: n }))
                         || [{ name: "" }],
      steps:             (draft.steps?.length ? draft.steps : null)
                         || parseSchemaSteps(source?.schema_recipe?.recipeInstructions)
                         || [{ text: "" }],
      diet_flags:        draft.diet_flags       ?? [],
      allergens:         draft.allergens        ?? [],
      tags:              (draft.tags && Array.isArray(draft.tags))
                         ? draft.tags.join(", ")
                         : (Array.isArray(source?.schema_recipe?.keywords)
                            ? source.schema_recipe.keywords.join(", ")
                            : source?.schema_recipe?.keywords || ""),
      notes:             draft.notes            ?? "",
      video_url:         draft.video_url        ?? (source?.platform === "youtube" || source?.platform === "tiktok" ? source?.canonical_url || "" : ""),
      website_url:       draft.website_url      ?? (source?.platform !== "youtube" && source?.platform !== "tiktok" ? source?.canonical_url || "" : ""),
      published_at:      draft.published_at     ?? source?.published_at ?? "",
    };
  }, [source]);

  const [form, setForm] = useState(initial);
  const [busy, setBusy]     = useState(null);   // null | "draft" | "publish" | "ai"
  const [msg,  setMsg]      = useState(null);   // { kind, text }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setIngredient = (i, patch) =>
    setForm((f) => {
      const arr = [...f.ingredients];
      arr[i] = { ...arr[i], ...patch };
      return { ...f, ingredients: arr };
    });
  const addIngredient    = () => setForm((f) => ({ ...f, ingredients: [...f.ingredients, { name: "" }] }));
  const removeIngredient = (i) => setForm((f) => ({ ...f, ingredients: f.ingredients.filter((_, idx) => idx !== i) }));
  const setStep    = (i, text) => setForm((f) => { const arr = [...f.steps]; arr[i] = { ...arr[i], text }; return { ...f, steps: arr }; });
  const addStep    = () => setForm((f) => ({ ...f, steps: [...f.steps, { text: "" }] }));
  const removeStep = (i) => setForm((f) => ({ ...f, steps: f.steps.filter((_, idx) => idx !== i) }));

  const toggleInArray = (key, value) => setForm((f) => {
    const arr = new Set(f[key] || []);
    if (arr.has(value)) arr.delete(value); else arr.add(value);
    return { ...f, [key]: Array.from(arr) };
  });

  const buildPayload = () => ({
    title:             form.title || null,
    slug:              form.slug || null,
    short_description: form.short_description || null,
    story:             form.story || null,
    cover_photo:       form.cover_photo || null,
    cuisine:           form.cuisine || null,
    country:           form.country || null,
    category:          form.category || null,
    difficulty:        form.difficulty || null,
    prep_time_min:     form.prep_time_min  === "" ? null : Number(form.prep_time_min),
    cook_time_min:     form.cook_time_min  === "" ? null : Number(form.cook_time_min),
    total_time_min:    form.total_time_min === "" ? null : Number(form.total_time_min),
    servings:          form.servings       === "" ? null : Number(form.servings),
    ingredients:       form.ingredients.filter((i) => i.name?.trim())
                          .map((i) => ({ name: i.name.trim(), quantity: i.quantity || null,
                                          unit: i.unit || null, food_item_slug: i.food_item_slug || null,
                                          optional: !!i.optional })),
    steps:             form.steps.filter((s) => s.text?.trim())
                          .map((s) => ({ text: s.text.trim(),
                                          duration_min: s.duration_min || null })),
    diet_flags:        form.diet_flags,
    allergens:         form.allergens,
    tags:              String(form.tags || "").split(",").map((t) => t.trim()).filter(Boolean),
    notes:             form.notes || null,
    video_url:         form.video_url || null,
    website_url:       form.website_url || null,
    published_at:      form.published_at || null,
  });

  const doSaveDraft = async () => {
    setBusy("draft"); setMsg(null);
    try {
      await http.put(`/recipe-discovery/sources/${source.id}/draft`, buildPayload());
      setMsg({ kind: "ok", text: "Draft saved." });
      onSaved && onSaved(source);
    } catch (e) {
      setMsg({ kind: "err", text: e?.response?.data?.detail || e.message });
    } finally { setBusy(null); }
  };

  const doPublish = async () => {
    if (!form.title?.trim()) { setMsg({ kind: "err", text: "Title is required to publish." }); return; }
    if (!form.ingredients.some((i) => i.name?.trim())) {
      setMsg({ kind: "err", text: "At least one ingredient is required." }); return;
    }
    if (!window.confirm(`Publish "${form.title}" to /food-pulse/recipes now?`)) return;
    setBusy("publish"); setMsg(null);
    try {
      const r = await http.post(`/recipe-discovery/sources/${source.id}/publish`, buildPayload());
      setMsg({ kind: "ok", text: `Published → /recipes/${r.data.slug}` });
      onPublished && onPublished(r.data);
    } catch (e) {
      setMsg({ kind: "err", text: e?.response?.data?.detail || e.message });
    } finally { setBusy(null); }
  };

  const doAIAssist = async () => {
    setBusy("ai"); setMsg(null);
    try {
      const r = await http.post(`/recipe-discovery/sources/${source.id}/ai-assist`);
      if (r.data.budget_exhausted) {
        setMsg({ kind: "warn", text: "LLM daily spend limit reached. Please edit manually or retry after budget reset." });
        return;
      }
      const d = r.data.draft || {};
      // Merge AI-filled fields into the form (never overwrite user edits — but
      // this is a one-shot fill so we do overwrite blanks only).
      setForm((f) => ({
        ...f,
        title:             f.title             || d.title             || f.title,
        short_description: f.short_description || d.short_description || f.short_description,
        cuisine:           f.cuisine           || d.cuisine           || f.cuisine,
        country:           f.country           || d.country           || f.country,
        category:          f.category          || d.category          || f.category,
        difficulty:        f.difficulty        || d.difficulty        || f.difficulty,
        prep_time_min:     f.prep_time_min !== "" ? f.prep_time_min : (d.prep_time_min ?? ""),
        cook_time_min:     f.cook_time_min !== "" ? f.cook_time_min : (d.cook_time_min ?? ""),
        servings:          f.servings !== ""      ? f.servings      : (d.servings      ?? ""),
        ingredients:       (f.ingredients.some((i) => i.name?.trim()) ? f.ingredients : (d.ingredients || f.ingredients)),
        steps:             (f.steps.some((s) => s.text?.trim())       ? f.steps       : (d.steps       || f.steps)),
        diet_flags:        (f.diet_flags?.length ? f.diet_flags : (d.diet_flags || [])),
        allergens:         (f.allergens?.length  ? f.allergens  : (d.allergens  || [])),
        tags:              (f.tags?.trim() ? f.tags : (Array.isArray(d.tags) ? d.tags.join(", ") : (d.tags || ""))),
      }));
      setMsg({ kind: "ok", text: `AI filled ${Object.keys(d).length} fields.` });
    } catch (e) {
      setMsg({ kind: "err", text: e?.response?.data?.detail || e.message });
    } finally { setBusy(null); }
  };

  const Content = (
    <div className="grid lg:grid-cols-[1fr_320px] gap-6" data-testid={`recipe-editor-${source.id}`}>
      {/* ── LEFT: main editor ─────────────────────────────── */}
      <div className="space-y-5">
        {/* Title + slug */}
        <div className="space-y-2">
          <label className="text-[10px] font-mono uppercase tracking-widest opacity-70">Title</label>
          <input value={form.title}
                 onChange={(e) => set("title", e.target.value)}
                 placeholder="e.g. Kerala Fish Molee"
                 className="w-full border rounded-lg px-3 py-2.5 text-base font-display"
                 style={{ borderColor: "var(--wofy-line)" }}
                 data-testid="recipe-editor-title" />
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-mono opacity-60">/food-pulse/recipes/</span>
            <input value={form.slug}
                   onChange={(e) => set("slug", slugify(e.target.value))}
                   placeholder={slugify(form.title) || "auto-generated"}
                   className="flex-1 border rounded px-2 py-1 text-xs font-mono"
                   style={{ borderColor: "var(--wofy-line)" }}
                   data-testid="recipe-editor-slug" />
          </div>
        </div>

        {/* Descriptions */}
        <div className="space-y-2">
          <label className="text-[10px] font-mono uppercase tracking-widest opacity-70">
            Short description <span className="opacity-60">— appears on recipe cards</span>
          </label>
          <textarea rows={2}
                    value={form.short_description}
                    onChange={(e) => set("short_description", e.target.value)}
                    placeholder="1–2 sentences that hook the reader."
                    className="w-full border rounded-lg px-3 py-2.5 text-sm"
                    style={{ borderColor: "var(--wofy-line)" }}
                    data-testid="recipe-editor-short-desc" />
        </div>
        <div className="space-y-2">
          <label className="text-[10px] font-mono uppercase tracking-widest opacity-70">Story / background</label>
          <textarea rows={4}
                    value={form.story}
                    onChange={(e) => set("story", e.target.value)}
                    placeholder="Longer story, origin, cultural notes."
                    className="w-full border rounded-lg px-3 py-2.5 text-sm"
                    style={{ borderColor: "var(--wofy-line)" }}
                    data-testid="recipe-editor-story" />
        </div>

        {/* Ingredients */}
        <fieldset className="border rounded-lg p-4" style={{ borderColor: "var(--wofy-line)" }}>
          <legend className="text-xs font-mono uppercase tracking-widest px-2 opacity-70">Ingredients</legend>
          <div className="space-y-2">
            {form.ingredients.map((ing, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`recipe-editor-ing-row-${i}`}>
                <GripVertical size={12} className="opacity-30" />
                <input value={ing.quantity || ""}
                       onChange={(e) => setIngredient(i, { quantity: e.target.value })}
                       placeholder="Qty" className="w-16 border rounded px-2 py-1 text-xs"
                       style={{ borderColor: "var(--wofy-line)" }} />
                <input value={ing.unit || ""}
                       onChange={(e) => setIngredient(i, { unit: e.target.value })}
                       placeholder="Unit" className="w-20 border rounded px-2 py-1 text-xs"
                       style={{ borderColor: "var(--wofy-line)" }} />
                <input value={ing.name}
                       onChange={(e) => setIngredient(i, { name: e.target.value })}
                       placeholder={`Ingredient ${i + 1}`}
                       className="flex-1 border rounded px-2 py-1 text-sm"
                       style={{ borderColor: "var(--wofy-line)" }}
                       data-testid={`recipe-editor-ing-name-${i}`} />
                <input value={ing.food_item_slug || ""}
                       onChange={(e) => setIngredient(i, { food_item_slug: e.target.value })}
                       placeholder="food_item slug"
                       className="w-32 border rounded px-2 py-1 text-[11px] font-mono"
                       style={{ borderColor: "var(--wofy-line)" }} />
                <button onClick={() => removeIngredient(i)}
                        className="text-xs opacity-60 hover:text-red-600"
                        data-testid={`recipe-editor-ing-remove-${i}`}
                        aria-label="Remove ingredient">
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
          <button onClick={addIngredient}
                  className="mt-2 inline-flex items-center gap-1 text-xs border rounded-full px-3 py-1"
                  style={{ borderColor: "var(--wofy-line)" }}
                  data-testid="recipe-editor-ing-add">
            <Plus size={11} /> Add ingredient
          </button>
        </fieldset>

        {/* Steps */}
        <fieldset className="border rounded-lg p-4" style={{ borderColor: "var(--wofy-line)" }}>
          <legend className="text-xs font-mono uppercase tracking-widest px-2 opacity-70">Method / steps</legend>
          <ol className="space-y-2">
            {form.steps.map((s, i) => (
              <li key={i} className="flex items-start gap-2" data-testid={`recipe-editor-step-row-${i}`}>
                <span className="mt-2 text-xs font-mono opacity-60 w-6 text-right">{i + 1}.</span>
                <textarea rows={2}
                          value={s.text}
                          onChange={(e) => setStep(i, e.target.value)}
                          placeholder={`Step ${i + 1}…`}
                          className="flex-1 border rounded px-2 py-1.5 text-sm"
                          style={{ borderColor: "var(--wofy-line)" }}
                          data-testid={`recipe-editor-step-text-${i}`} />
                <button onClick={() => removeStep(i)}
                        className="mt-2 text-xs opacity-60 hover:text-red-600"
                        aria-label="Remove step">
                  <Trash2 size={12} />
                </button>
              </li>
            ))}
          </ol>
          <button onClick={addStep}
                  className="mt-2 inline-flex items-center gap-1 text-xs border rounded-full px-3 py-1"
                  style={{ borderColor: "var(--wofy-line)" }}
                  data-testid="recipe-editor-step-add">
            <Plus size={11} /> Add step
          </button>
        </fieldset>

        {/* Notes */}
        <div className="space-y-2">
          <label className="text-[10px] font-mono uppercase tracking-widest opacity-70">
            Notes <span className="opacity-60">— tips, variations, substitutions</span>
          </label>
          <textarea rows={3}
                    value={form.notes}
                    onChange={(e) => set("notes", e.target.value)}
                    className="w-full border rounded-lg px-3 py-2.5 text-sm"
                    style={{ borderColor: "var(--wofy-line)" }}
                    data-testid="recipe-editor-notes" />
        </div>
      </div>

      {/* ── RIGHT: sidebar metadata ────────────────────────── */}
      <aside className="space-y-4">
        {/* AI-assist */}
        <button onClick={doAIAssist} disabled={busy !== null}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
                style={{ background: "#5C6B52" }}
                data-testid="recipe-editor-ai-assist">
          {busy === "ai" ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          Enrich with AI (fills blanks)
        </button>

        {/* Cover photo */}
        <div className="rounded-lg border p-3" style={{ borderColor: "var(--wofy-line)" }}>
          <div className="text-[10px] font-mono uppercase tracking-widest opacity-70 mb-2">Cover photo URL</div>
          {form.cover_photo ? (
            <img src={form.cover_photo} alt="cover"
                 className="w-full h-32 rounded object-cover mb-2"
                 onError={(e) => { e.currentTarget.style.display = "none"; }} />
          ) : (
            <div className="w-full h-32 rounded flex items-center justify-center opacity-40 mb-2"
                 style={{ background: "#F2ECDE" }}>
              <ImageIcon size={28} />
            </div>
          )}
          <input value={form.cover_photo}
                 onChange={(e) => set("cover_photo", e.target.value)}
                 placeholder="https://…"
                 className="w-full border rounded px-2 py-1 text-xs font-mono"
                 style={{ borderColor: "var(--wofy-line)" }}
                 data-testid="recipe-editor-cover" />
        </div>

        {/* Cuisine / country / category */}
        <div className="rounded-lg border p-3 space-y-2" style={{ borderColor: "var(--wofy-line)" }}>
          <div className="text-[10px] font-mono uppercase tracking-widest opacity-70">Classification</div>
          <input value={form.cuisine}    onChange={(e) => set("cuisine", e.target.value)}
                 placeholder="Cuisine (e.g. Kerala, Italian)"
                 className="w-full border rounded px-2 py-1 text-xs"
                 style={{ borderColor: "var(--wofy-line)" }} data-testid="recipe-editor-cuisine" />
          <input value={form.country}    onChange={(e) => set("country", e.target.value)}
                 placeholder="Country of origin"
                 className="w-full border rounded px-2 py-1 text-xs"
                 style={{ borderColor: "var(--wofy-line)" }} />
          <input value={form.category}   onChange={(e) => set("category", e.target.value)}
                 placeholder="Category (Main, Dessert…)"
                 className="w-full border rounded px-2 py-1 text-xs"
                 style={{ borderColor: "var(--wofy-line)" }} data-testid="recipe-editor-category" />
        </div>

        {/* Timings */}
        <div className="rounded-lg border p-3 space-y-2" style={{ borderColor: "var(--wofy-line)" }}>
          <div className="text-[10px] font-mono uppercase tracking-widest opacity-70">Timings & servings</div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-[10px] opacity-60">Prep (min)</div>
              <input type="number" min="0" value={form.prep_time_min}
                     onChange={(e) => set("prep_time_min", e.target.value)}
                     className="w-full border rounded px-2 py-1 text-xs"
                     style={{ borderColor: "var(--wofy-line)" }}
                     data-testid="recipe-editor-prep-min" />
            </div>
            <div>
              <div className="text-[10px] opacity-60">Cook (min)</div>
              <input type="number" min="0" value={form.cook_time_min}
                     onChange={(e) => set("cook_time_min", e.target.value)}
                     className="w-full border rounded px-2 py-1 text-xs"
                     style={{ borderColor: "var(--wofy-line)" }}
                     data-testid="recipe-editor-cook-min" />
            </div>
            <div>
              <div className="text-[10px] opacity-60">Servings</div>
              <input type="number" min="0" value={form.servings}
                     onChange={(e) => set("servings", e.target.value)}
                     className="w-full border rounded px-2 py-1 text-xs"
                     style={{ borderColor: "var(--wofy-line)" }}
                     data-testid="recipe-editor-servings" />
            </div>
            <div>
              <div className="text-[10px] opacity-60">Difficulty</div>
              <select value={form.difficulty}
                      onChange={(e) => set("difficulty", e.target.value)}
                      className="w-full border rounded px-2 py-1 text-xs"
                      style={{ borderColor: "var(--wofy-line)" }}
                      data-testid="recipe-editor-difficulty">
                {DIFFICULTIES.map((d) => <option key={d}>{d}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Diet flags */}
        <div className="rounded-lg border p-3" style={{ borderColor: "var(--wofy-line)" }}>
          <div className="text-[10px] font-mono uppercase tracking-widest opacity-70 mb-2">Diet flags</div>
          <div className="flex flex-wrap gap-1.5">
            {DIET_FLAGS.map((d) => (
              <button key={d} type="button"
                      onClick={() => toggleInArray("diet_flags", d)}
                      data-testid={`recipe-editor-diet-${slugify(d)}`}
                      className={`text-[11px] rounded-full px-2 py-0.5 border ${form.diet_flags?.includes(d) ? "font-semibold" : "opacity-70"}`}
                      style={{
                        borderColor: form.diet_flags?.includes(d) ? "#3E5A2A" : "var(--wofy-line)",
                        background:  form.diet_flags?.includes(d) ? "#3E5A2A22" : "transparent",
                        color:       form.diet_flags?.includes(d) ? "#3E5A2A" : "var(--wofy-text)",
                      }}>
                {d}
              </button>
            ))}
          </div>
        </div>

        {/* Allergens */}
        <div className="rounded-lg border p-3" style={{ borderColor: "var(--wofy-line)" }}>
          <div className="text-[10px] font-mono uppercase tracking-widest opacity-70 mb-2">Allergens</div>
          <div className="flex flex-wrap gap-1.5">
            {ALLERGENS.map((a) => (
              <button key={a} type="button"
                      onClick={() => toggleInArray("allergens", a)}
                      data-testid={`recipe-editor-allergen-${slugify(a)}`}
                      className={`text-[11px] rounded-full px-2 py-0.5 border ${form.allergens?.includes(a) ? "font-semibold" : "opacity-70"}`}
                      style={{
                        borderColor: form.allergens?.includes(a) ? "#B04A3E" : "var(--wofy-line)",
                        background:  form.allergens?.includes(a) ? "#B04A3E22" : "transparent",
                        color:       form.allergens?.includes(a) ? "#B04A3E" : "var(--wofy-text)",
                      }}>
                {a}
              </button>
            ))}
          </div>
        </div>

        {/* Tags */}
        <div className="rounded-lg border p-3" style={{ borderColor: "var(--wofy-line)" }}>
          <div className="text-[10px] font-mono uppercase tracking-widest opacity-70 mb-2">
            Tags <span className="opacity-60">— comma separated</span>
          </div>
          <input value={form.tags}
                 onChange={(e) => set("tags", e.target.value)}
                 placeholder="quick, weeknight, one-pot"
                 className="w-full border rounded px-2 py-1 text-xs"
                 style={{ borderColor: "var(--wofy-line)" }}
                 data-testid="recipe-editor-tags" />
        </div>

        {/* Publish date */}
        <div className="rounded-lg border p-3" style={{ borderColor: "var(--wofy-line)" }}>
          <div className="text-[10px] font-mono uppercase tracking-widest opacity-70 mb-2">Publish date</div>
          <input type="datetime-local"
                 value={form.published_at ? form.published_at.slice(0, 16) : ""}
                 onChange={(e) => set("published_at", e.target.value ? new Date(e.target.value).toISOString() : "")}
                 className="w-full border rounded px-2 py-1 text-xs"
                 style={{ borderColor: "var(--wofy-line)" }}
                 data-testid="recipe-editor-published-at" />
        </div>

        {/* Source attribution — read-only */}
        <div className="rounded-lg border p-3 text-[11px]" style={{ borderColor: "var(--wofy-line)", background: "#FBFBF6" }}>
          <div className="text-[10px] font-mono uppercase tracking-widest opacity-70 mb-2">Attribution (locked)</div>
          <div className="opacity-80"><ChefHat size={11} className="inline mr-1" />
            {source?.creator_name || source?.channel_name || "Unknown creator"}
          </div>
          <a href={source?.canonical_url || source?.original_url} target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-1 mt-1 underline hover:opacity-80"
             data-testid="recipe-editor-source-link">
            <ExternalLink size={10} /> View original
          </a>
        </div>
      </aside>
    </div>
  );

  const Footer = (
    <div className="flex items-center justify-between gap-3 pt-4 mt-4 border-t"
         style={{ borderColor: "var(--wofy-line)" }}>
      {msg && (
        <div className="text-xs" data-testid="recipe-editor-msg" style={{
          color: msg.kind === "err" ? "#B04A3E" : msg.kind === "warn" ? "#8A5A24" : "#3E5A2A",
        }}>
          {msg.kind === "err" && <AlertTriangle size={12} className="inline mr-1" />}
          {msg.text}
        </div>
      )}
      <div className="ml-auto flex items-center gap-2">
        {variant === "modal" && (
          <button onClick={onClose} disabled={busy !== null}
                  className="text-sm px-3 py-2 rounded-full border disabled:opacity-50"
                  style={{ borderColor: "var(--wofy-line)" }}
                  data-testid="recipe-editor-cancel">Close</button>
        )}
        <button onClick={doSaveDraft} disabled={busy !== null}
                className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full border disabled:opacity-50"
                style={{ borderColor: "var(--wofy-line)" }}
                data-testid="recipe-editor-save-draft">
          {busy === "draft" ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
          Save draft
        </button>
        <button onClick={doPublish} disabled={busy !== null}
                className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full text-white disabled:opacity-50"
                style={{ background: "#3E5A2A" }}
                data-testid="recipe-editor-publish">
          {busy === "publish" ? <Loader2 size={13} className="animate-spin" /> : <Rocket size={13} />}
          Publish
        </button>
      </div>
    </div>
  );

  if (variant === "modal") {
    return (
      <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto"
           data-testid={`recipe-editor-modal-${source.id}`}
           onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}>
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-6xl my-8">
          <div className="flex items-center justify-between px-6 py-4 border-b"
               style={{ borderColor: "var(--wofy-line)" }}>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest opacity-70">Recipe Editor</div>
              <div className="font-display text-xl mt-0.5">
                {form.title || source?.title || "(untitled)"}
              </div>
            </div>
            <button onClick={onClose} aria-label="Close" className="opacity-60 hover:opacity-100"
                    data-testid="recipe-editor-close-btn">
              <X size={20} />
            </button>
          </div>
          <div className="px-6 pt-4 pb-2">
            {Content}
          </div>
          <div className="px-6 pb-4">{Footer}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border p-5" style={{ borderColor: "var(--wofy-line)" }}>
      {Content}
      {Footer}
    </div>
  );
}

function parseSchemaSteps(ri) {
  if (!ri) return null;
  if (typeof ri === "string") {
    return ri.split(/\r?\n|\.\s+/).map((t) => t.trim()).filter((t) => t.length > 5)
             .map((t) => ({ text: t }));
  }
  if (Array.isArray(ri)) {
    return ri.map((e) => typeof e === "string" ? { text: e } : { text: e?.text || e?.name || "" })
             .filter((s) => s.text);
  }
  return null;
}
